'use client'
import React, { useEffect, useRef } from 'react'
import * as Sentry from '@sentry/nextjs'
import { useRouter, usePathname } from 'next/navigation'
import { toSafeInternalPath } from '@/utils/url'
import { useUser } from '@/hooks/users/useUser'
import { useActorScript } from '@/hooks/cinematics/useActorScript'
import { AuthActor } from '@/actors/AuthActor'
import { IAuthScript } from '@/actors/AuthActor/types'
import { LAYOUT_TYPE } from '@/constants/auth'
import { useTranslation } from '@/localization/useTranslation'
import settings from '@/settings/public'
import { debug } from '@/utils/debug'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCircleUser } from '@fortawesome/free-solid-svg-icons'
import { Button } from '@/components/Buttons/Button'
import { Spinner } from '@/components/Loading/Spinner'

import classes from './styles.module.scss'

type AuthRequirement = 'authenticated' | 'anonymous'
type AuthFallback = 'redirect' | 'login' | 'message' | 'hidden'

interface IAuthGuardProps {
  children: React.ReactNode
  require: AuthRequirement
  fallback?: AuthFallback
  redirectTo?: string
  message?: React.ReactNode | ((data: { require: AuthRequirement, isLoggedIn: boolean }) => React.ReactNode) | null | false
  skeleton?: React.ReactNode
}

interface DefaultAuthBlockMessageProps {
  require: AuthRequirement
}

// Strip query/hash/trailing-slash so `/profile` and `/profile/` (or
// `/login?next=x` on `/login`) compare equal — a raw `===` misses those and
// lets `router.replace` re-fire forever.
const normalizePath = (path: string): string => {
  try {
    const base = typeof window !== 'undefined' ? window.location.origin : 'http://x'
    return new URL(path, base).pathname.replace(/\/+$/, '') || '/'
  } catch {
    return path
  }
}

const DefaultAuthBlockMessage: React.FC<DefaultAuthBlockMessageProps> = ({ require }) => {
  const { t } = useTranslation()
  const router = useRouter()
  const appTitle = settings.app?.title

  const handleAction = () => {
    router.push(require === 'authenticated' ? '/login' : '/')
  }

  debug.log({ require })

  return (
    <div className={classes.container}>
      <FontAwesomeIcon size='3x' icon={faCircleUser} />
      <h3>{require === 'authenticated' ? t('Log in to {appTitle}', { appTitle }) : t('You are already logged in')}</h3>
      <p>
        {require === 'authenticated'
          ? t('Log in or register to access this section\'s content')
          : t('Log out to access this page')}
      </p>
      <Button onClick={handleAction}>
        {require === 'authenticated' ? t('Enter {appTitle}', { appTitle }) : t('Back to home')}
      </Button>
    </div>
  )
}

export const AuthGuard = (props: IAuthGuardProps) => {
  const { children, require, fallback, redirectTo, message, skeleton } = props
  const { isLoggedIn, isLoading } = useUser()
  const router = useRouter()
  const pathname = usePathname()
  const { script: authScript } = useActorScript('Auth')

  const isMet = require === 'authenticated' ? isLoggedIn : !isLoggedIn
  // `login` renders an inline auth panel in place of the gated content — that
  // only makes sense for `authenticated`. An `anonymous` gate has no "log out
  // in place" equivalent, so it degrades to `redirect`.
  const effectiveFallback: Exclude<AuthFallback, 'login'> | 'login' =
    fallback === 'login' && require === 'anonymous' ? 'redirect' : (fallback ?? 'redirect')

  const defaultTarget = require === 'authenticated' ? '/login' : '/'
  // `redirectTo` comes verbatim from remote cinematics config. Only a
  // same-origin path may reach the router; an off-origin/`javascript:` value
  // falls back to the safe default (a config mistake worth a Sentry warning,
  // emitted from the effect so render stays side-effect-free).
  const safeRedirect = redirectTo ? toSafeInternalPath(redirectTo) : null
  const target = safeRedirect || defaultTarget
  const redirectRejected = Boolean(redirectTo) && safeRedirect === null
  // A scene whose own auth-gated route is also its redirect target would loop
  // forever — compare normalized paths so query/trailing-slash variants can't
  // slip past — and fall back to the block message instead of navigating.
  const isRedirectLoop = normalizePath(target) === normalizePath(pathname)

  const hasWarnedRef = useRef(false)

  useEffect(() => {
    if (isLoading || isMet) return
    if (redirectRejected && !hasWarnedRef.current) {
      hasWarnedRef.current = true
      Sentry.captureException(
        new Error(`AuthGuard: rejected off-origin authRedirect "${redirectTo}"`),
        { level: 'warning' }
      )
    }
    if (effectiveFallback === 'redirect' && !isRedirectLoop) {
      router.replace(target)
    }
  }, [isLoading, isMet, effectiveFallback, isRedirectLoop, target, router, redirectRejected, redirectTo])

  if (isLoading) {
    // Same rule as GeoLocationGuard: never render a blank page while auth
    // state resolves, and never render gated children early either.
    return skeleton
      ? <>{skeleton}</>
      : (
        <div className={classes.loading}>
          <Spinner />
        </div>
      )
  }

  if (!isMet) {
    if (effectiveFallback === 'redirect') {
      return isRedirectLoop
        ? <DefaultAuthBlockMessage require={require} />
        : null
    }

    if (effectiveFallback === 'login') {
      return (
        <div className={classes.auth}>
          <AuthActor
            script={authScript as IAuthScript}
            redirectOnAuthenticated={false}
            initialLayoutType={LAYOUT_TYPE.ISLAND}
          />
        </div>
      )
    }

    if (effectiveFallback === 'hidden') {
      return null
    }

    // effectiveFallback === 'message'
    if (message === null || message === false) {
      return null
    }

    if (message !== undefined) {
      const messageContent = typeof message === 'function'
        ? message({ require, isLoggedIn })
        : message

      return (
        <>
          {messageContent}
        </>
      )
    }

    return <DefaultAuthBlockMessage require={require} />
  }

  return (
    <>
      {children}
    </>
  )
}
